import { cn } from "@/lib/utils";
import {
  BatteryFull,
  CalendarDays,
  Clock,
  Ellipsis,
  House,
  Signal,
  Wifi,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

// Phone bezel plus the app chrome (status bar and bottom navigation).
// The app is laid out at its real 375px width and scaled down to fit the bezel,
// so the screens can use the same sizes as the real components.

export type NavKey = "today" | "agenda" | "marks" | "more";

export type NavLabels = {
  today: string;
  agenda: string;
  marks: string;
  more: string;
};

const APP_WIDTH = 375;
const APP_HEIGHT = 739;
const SCREEN_WIDTH = 280;

const NAV_ITEMS = [
  { key: "today", Icon: House },
  { key: "agenda", Icon: CalendarDays },
  { key: "marks", Icon: Clock },
  { key: "more", Icon: Ellipsis },
] as const;

export const PHONE_WIDTH = SCREEN_WIDTH + 8;
export const PHONE_HEIGHT = 552 + 8;

export function PhoneFrame({
  nav,
  labels,
  scale,
  screenKey,
  children,
}: {
  nav: NavKey;
  labels: NavLabels;
  scale: number;
  /** Changes with the screen so the scroll position starts at the top each time. */
  screenKey: number;
  children: ReactNode;
}) {
  // `zoom` scales the layout box too, which is what keeps the page flow correct.
  // The frame sits above the site's cursor spotlight (z-30) so it does not tint the screens.
  const frameStyle: CSSProperties = { zoom: scale };
  const appStyle: CSSProperties = {
    width: APP_WIDTH,
    height: APP_HEIGHT,
    transform: `scale(${SCREEN_WIDTH / APP_WIDTH})`,
  };

  return (
    <div
      style={frameStyle}
      className="relative z-40 w-[288px] shrink-0 rounded-[30px] border-4 border-neutral-900 bg-neutral-900 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6),0_0_80px_rgba(245,158,11,0.1)] ring-1 ring-white/10"
    >
      <div
        className="absolute left-1/2 top-[6px] z-30 h-[11px] w-[54px] -translate-x-1/2 rounded-full bg-neutral-900"
        aria-hidden
      />
      <div className="relative h-[552px] w-[280px] overflow-hidden rounded-[25px] bg-[#f3f4f6]">
        <div
          style={appStyle}
          className="flex origin-top-left flex-col bg-[#f3f4f6] text-left font-sans text-base leading-normal text-[#333]"
        >
          <div
            className="flex h-[34px] shrink-0 items-end justify-between pb-1 pl-8 pr-[30px] text-sm font-semibold"
            aria-hidden
          >
            <span>9:41</span>
            <span className="flex items-center gap-1.5">
              <Signal size={15} />
              <Wifi size={15} />
              <BatteryFull size={15} />
            </span>
          </div>

          <div
            key={screenKey}
            // Small screens: no inner scroll, so the page keeps scrolling when the pointer or
            // finger is over the phone. Large screens: scrollable, and it hands over to the
            // page once it reaches the end.
            className="min-h-0 flex-1 overflow-hidden [scrollbar-width:none] lg:overflow-y-auto [&::-webkit-scrollbar]:hidden"
          >
            {children}
          </div>

          <div
            className="flex shrink-0 gap-0.5 border-t border-[#e5e7eb] bg-white px-1 pb-2.5 pt-[7px]"
            aria-hidden
          >
            {NAV_ITEMS.map(({ key, Icon }) => (
              <span
                key={key}
                className={cn(
                  "flex flex-1 flex-col items-center gap-0.5 py-1 text-xs font-semibold transition-colors",
                  nav === key ? "font-bold text-[#ffa514]" : "text-[#808080]",
                )}
              >
                <Icon size={17} />
                {labels[key]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
